/**
 * View-model hook for outline collapse state (the outline twin of
 * useTranscriptCollapse).
 *
 * Bridges the app store's outline collapse scope to the outline component:
 * resolves the collapsed-id map (store state falling back to defaults),
 * derives the per-row accessors, and seeds the store with the defaults on
 * first mount so individual toggles preserve the other rows' default state.
 */

import { useEffect, useMemo } from "react";

import { type TranscriptCollapseState } from "../types";

export interface OutlineCollapseState {
  /** Current collapsed node IDs for the outline scope. */
  collapsed?: Record<string, boolean>;
  /** Collapse/expand a single outline node. */
  onCollapse?: (nodeId: string, collapsed: boolean) => void;
  /** Bulk-set outline collapsed state (for initialization). */
  onSetCollapsed?: (ids: Record<string, boolean>) => void;
}

/** Project the transcript-level collapse store down to the outline scope. */
export function outlineCollapseState(
  collapseState: TranscriptCollapseState | undefined
): OutlineCollapseState | undefined {
  return collapseState
    ? {
        collapsed: collapseState.outline,
        onCollapse: collapseState.onCollapseOutline,
        onSetCollapsed: collapseState.onSetOutlineCollapsed,
      }
    : undefined;
}

export interface OutlineCollapse {
  /** Collapsed-id map for outline row derivation (store state or defaults). */
  collapsedIds: Record<string, boolean>;
  /** Per-row collapsed accessor; undefined while the store is unseeded. */
  getCollapsed: ((id: string) => boolean) | undefined;
  /** Collapse/expand a single outline row. */
  setCollapsed: ((id: string, collapsed: boolean) => void) | undefined;
}

export function useOutlineCollapse(
  defaultCollapsedIds: Record<string, boolean>,
  collapse?: OutlineCollapseState
): OutlineCollapse {
  const collapsedEvents = collapse?.collapsed;
  const onSetCollapsed = collapse?.onSetCollapsed;

  // Initialize collapsed events from defaults
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (!collapsedEvents && Object.keys(defaultCollapsedIds).length > 0) {
      onSetCollapsed?.(defaultCollapsedIds);
    }
  }, [defaultCollapsedIds, collapsedEvents, onSetCollapsed]);

  const getCollapsed = useMemo(
    () =>
      collapsedEvents
        ? (id: string) => collapsedEvents[id] === true
        : undefined,
    [collapsedEvents]
  );

  return {
    collapsedIds: collapsedEvents ?? defaultCollapsedIds,
    getCollapsed,
    setCollapsed: collapse?.onCollapse,
  };
}
